/**
 * Cutting and patching pictures, in the browser (2026-10-06).
 *
 * Two things the board does to a picture, both of which end as a NEW picture
 * beside the old one; the original is never touched:
 *
 *   CROP  -- free and immediate. Cut here from the full-size file.
 *   EDIT  -- a box drawn in the Edit area card. The model (Image 2.5
 *            Sunburst, high) redraws what is in the box.
 *
 * (Expand -- the crop box pulled past the picture's edges -- was built and
 * taken out the same day: the model redrew the whole picture instead of
 * extending it, and the join could not be hidden.)
 *
 * Everything is worked out on the picture AS IT IS SEEN: a picture turned on
 * the board is cut or edited the way round it is shown, and the new picture
 * is saved that way round (Huanrui, 2026-10-06).
 *
 * WHY THE MODEL'S PICTURE IS NEVER THE RESULT
 * A model asked to change one part of a picture redraws all of it, a little
 * differently -- colours drift, a label's lettering moves. So the board sends
 * the model the part round the box, takes back what it drew, lines it up and
 * matches its colours (`fit`), and then lets only the masked part of it
 * through over the ORIGINAL. The mask starts as the box with a soft edge and
 * is painted with a brush on the card afterwards; outside it the result is
 * the original, pixel for pixel.
 *
 * The recipe for the first putting-together is saved on the waiting frame
 * (`ComposeRecipe`), so a board closed mid-run finishes the job when it is
 * opened again; the run itself is on the queue and never stopped.
 */

export interface Rect {
  x: number
  y: number
  w: number
  h: number
}

/**
 * The size the model is sent, and so the size it answers with: the model is
 * asked for 'auto', and Image 2.5 Sunburst answers 'auto' with a picture the
 * size of the one it was given (Huanrui tested this, 2026-10-06). The longer
 * side is kept between 1024 and 1536 -- a small box is enlarged so the model
 * has room to draw, a large area reduced to the most the model is known to
 * take -- and both sides are a multiple of 16. Whatever comes back is fitted
 * to the area it stands for, so a few pixels either way do not matter.
 */
function sendSize(area: { w: number; h: number }): { w: number; h: number } {
  const long = Math.max(area.w, area.h)
  const target = Math.min(1536, Math.max(1024, long))
  const k = target / long
  const round16 = (v: number) => Math.max(16, Math.round((v * k) / 16) * 16)
  return { w: round16(area.w), h: round16(area.h) }
}

export interface ComposeRecipe {
  /** 'expand' only on frames from the few hours it existed; nothing makes one now. */
  kind: 'expand' | 'edit'
  /** The picture it was made from, and how far it was turned. */
  source: { store: 'library' | 'project'; id: string }
  turn: number
  /** The turned picture's full size, in pixels. */
  pw: number
  ph: number
  /** The new picture, in the turned picture's pixels (x and y below 0 when extended up or left). */
  out: Rect
  /** Where the model's picture goes, in the same pixels. */
  region: Rect
  /** Edit: the box that changes. Expand: unused (the picture itself). */
  box: Rect
  /**
   * Edit text (2026-10-07): the lines that change, each with room round it,
   * inside `box`. When set, only these are let through over the original
   * (defaultMask), and everything else in the area -- the lines between them
   * included -- counts as kept when the answer is lined up (keptAt).
   */
  boxes?: Rect[]
  /** How wide the soft edge is, in pixels. */
  feather: number
  /** Expand: which sides were pushed out. */
  sides?: { left: boolean; top: boolean; right: boolean; bottom: boolean }
  /** The size the model was sent, and (once known) the size it answered with. Logged to learn 'auto'. */
  sent?: { w: number; h: number }
  got?: { w: number; h: number }
  /** Whether to save as PNG (the original was one) or JPEG. */
  png: boolean
  title: string
}

/* ---------------------------------------------------------------- *
 * Reading and writing pictures
 * ---------------------------------------------------------------- */

/** A link short enough to log: where it is, not its signature. */
function shortLink(url: string): string {
  try {
    const at = new URL(url)
    return `${at.host}${at.pathname.length > 60 ? `…${at.pathname.slice(-48)}` : at.pathname}`
  } catch {
    return url.slice(0, 80)
  }
}

const pause = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms))

/**
 * The picture as an <img> asking for leave to be read (crossOrigin), the way
 * the board shows it. Readable afterwards because every link we draw from
 * says any page may read it.
 */
function imageOf(url: string, waitMs = 90_000): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image()
    image.crossOrigin = 'anonymous'
    image.decoding = 'async'
    const timer = setTimeout(() => reject(new Error('no answer')), waitMs)
    image.onload = () => {
      clearTimeout(timer)
      resolve(image)
    }
    image.onerror = () => {
      clearTimeout(timer)
      reject(new Error('the picture would not load'))
    }
    image.src = url
  })
}

/**
 * A picture's pixels, from its link (2026-10-26). Fetched, as before -- the
 * bytes say whether it is a PNG -- and tried once more after a moment; then,
 * if that still fails, read as an <img> instead. A large picture once came
 * back 200 with its body cut off, every time, in one Chrome window, while
 * the board's <img> of it was fine; that window's own conversation with the
 * server, or something in that browser, refused the fetch. What went wrong
 * is said in the console, and a slow read is timed there too.
 */
export async function loadBitmap(url: string): Promise<{ bitmap: ImageBitmap; png: boolean }> {
  const started = performance.now()
  let why = ''
  for (let attempt = 0; attempt < 2; attempt += 1) {
    if (attempt > 0) await pause(800)
    try {
      const response = await fetch(url)
      if (!response.ok) {
        why = `HTTP ${response.status}`
        // An expired or missing picture will not come right by asking again.
        if (response.status === 403 || response.status === 404) break
        continue
      }
      const blob = await response.blob()
      const fetched = performance.now()
      const bitmap = await createImageBitmap(blob)
      const done = performance.now()
      if (blob.size > 1_500_000 || done - started > 1500) {
        console.info(
          `[WonderWorks] picture read: ${(blob.size / 1048576).toFixed(1)} MB, ${bitmap.width} x ${bitmap.height} -- ` +
            `download ${Math.round(fetched - started)} ms, decode ${Math.round(done - fetched)} ms`,
          shortLink(url),
        )
      }
      return { bitmap, png: blob.type === 'image/png' }
    } catch (error) {
      why = error instanceof Error ? error.message : String(error)
    }
  }
  try {
    const image = await imageOf(url)
    const bitmap = await createImageBitmap(image)
    console.warn(
      `[WonderWorks] picture read as an image after fetching failed (${why}) -- ${Math.round(performance.now() - started)} ms`,
      shortLink(url),
    )
    let png = false
    try {
      png = /\.png$/i.test(new URL(url).pathname)
    } catch {
      png = false
    }
    return { bitmap, png }
  } catch (error) {
    console.error('[WonderWorks] a picture could not be read', { link: shortLink(url), fetch: why, image: String(error) })
    throw new Error(`The picture could not be read (${why || 'no answer'})`)
  }
}

/**
 * A picture to look at on a card, at most `longest` pixels on its longer
 * side, the way round it is shown (2026-10-26): the board's drawing copy is
 * enough to see and to drag a box on -- the box is kept as shares of the
 * picture, so it lands in the same place on the full-size one.
 */
export async function previewOf(url: string, turn: number, longest = 1600): Promise<HTMLCanvasElement> {
  const { bitmap } = await loadBitmap(url)
  try {
    const k = Math.min(1, longest / Math.max(bitmap.width, bitmap.height, 1))
    if (k >= 1) return turnedCanvas(bitmap, turn)
    const { canvas, ctx } = canvasOf(bitmap.width * k, bitmap.height * k)
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height)
    const small = await createImageBitmap(canvas)
    try {
      return turnedCanvas(small, turn)
    } finally {
      small.close()
    }
  } finally {
    bitmap.close()
  }
}

function canvasOf(w: number, h: number): { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D } {
  const canvas = document.createElement('canvas')
  canvas.width = Math.max(1, Math.round(w))
  canvas.height = Math.max(1, Math.round(h))
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('This browser could not draw the picture')
  ctx.imageSmoothingQuality = 'high'
  return { canvas, ctx }
}

/** The picture the way round it is shown: 0, 90, 180 or 270 degrees. */
export function turnedCanvas(bitmap: ImageBitmap, turn: number): HTMLCanvasElement {
  const t = ((Math.round(turn / 90) % 4) + 4) % 4
  const sideways = t % 2 === 1
  const { canvas, ctx } = canvasOf(sideways ? bitmap.height : bitmap.width, sideways ? bitmap.width : bitmap.height)
  ctx.translate(canvas.width / 2, canvas.height / 2)
  ctx.rotate((t * Math.PI) / 2)
  ctx.drawImage(bitmap, -bitmap.width / 2, -bitmap.height / 2)
  return canvas
}

export function blobOf(canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob> {
  return new Promise((resolve, reject) =>
    canvas.toBlob((made) => (made ? resolve(made) : reject(new Error('The picture could not be saved'))), type, quality),
  )
}

/** The most the store takes in one file (routes/projectFiles.ts). */
const MAX_BYTES = 25 * 1024 * 1024

/**
 * As a file to upload: PNG when the original was one (it may be a cut-out),
 * JPEG otherwise -- what the platform keeps photographs as. A PNG over the
 * store's limit goes as JPEG instead.
 */
export async function fileOf(canvas: HTMLCanvasElement, title: string, png: boolean): Promise<File> {
  const stem = (title || 'picture').replace(/\.[a-z0-9]+$/i, '').replace(/[\\/:*?"<>|]/g, '').trim() || 'picture'
  if (png) {
    const blob = await blobOf(canvas, 'image/png')
    if (blob.size <= MAX_BYTES) return new File([blob], `${stem}.png`, { type: 'image/png' })
  }
  const blob = await blobOf(canvas, 'image/jpeg', 0.92)
  return new File([blob], `${stem}.jpg`, { type: 'image/jpeg' })
}

/* ---------------------------------------------------------------- *
 * Crop
 * ---------------------------------------------------------------- */

/** The part of the (turned) picture inside `rect`, which lies within it. */
export function cropped(picture: HTMLCanvasElement, rect: Rect): HTMLCanvasElement {
  const r = clampInside(rect, picture.width, picture.height)
  const { canvas, ctx } = canvasOf(r.w, r.h)
  ctx.drawImage(picture, r.x, r.y, r.w, r.h, 0, 0, canvas.width, canvas.height)
  return canvas
}

function clampInside(rect: Rect, w: number, h: number): Rect {
  const x = Math.max(0, Math.round(rect.x))
  const y = Math.max(0, Math.round(rect.y))
  const x1 = Math.min(w, Math.round(rect.x + rect.w))
  const y1 = Math.min(h, Math.round(rect.y + rect.h))
  return { x, y, w: Math.max(1, x1 - x), h: Math.max(1, y1 - y) }
}

/* ---------------------------------------------------------------- *
 * What is sent to the model
 * ---------------------------------------------------------------- */

export interface Prepared {
  /** The picture the model sees. */
  request: HTMLCanvasElement
  /** The same size: opaque where nothing may change, transparent where it may. */
  mask: HTMLCanvasElement
  /** Always 'auto': the model answers at the size it was sent. */
  size: string
  recipe: ComposeRecipe
  /** Turned-picture pixels per pixel the model draws. Over 2, the new part is softer. */
  stretch: number
}

/** Draw `area` of the picture (picture pixels) onto a canvas of the size sent. */
function sendable(picture: HTMLCanvasElement, area: Rect, size: { w: number; h: number }) {
  const { canvas, ctx } = canvasOf(size.w, size.h)
  // One scale for both sides; the rounding to 16 is absorbed by stretching
  // the area a hair, the same way the answer is fitted back onto it.
  const kx = size.w / area.w
  const ky = size.h / area.h
  ctx.drawImage(picture, -area.x * kx, -area.y * ky, picture.width * kx, picture.height * ky)
  return { canvas, k: kx, ky }
}

/** A mask the size sent: opaque in `keep` (picture pixels), transparent elsewhere; or opaque but for `edit`. */
function maskOf(size: { w: number; h: number }, area: Rect, keep: Rect | null, edit: Rect | null) {
  const { canvas, ctx } = canvasOf(size.w, size.h)
  const kx = size.w / area.w
  const ky = size.h / area.h
  ctx.fillStyle = '#000000'
  if (keep) {
    ctx.fillRect((keep.x - area.x) * kx, (keep.y - area.y) * ky, keep.w * kx, keep.h * ky)
  } else {
    ctx.fillRect(0, 0, size.w, size.h)
  }
  if (edit) ctx.clearRect((edit.x - area.x) * kx, (edit.y - area.y) * ky, edit.w * kx, edit.h * ky)
  return canvas
}

/** Edit: `box` is what changes, in the turned picture's pixels. */
export function prepareEdit(
  picture: HTMLCanvasElement,
  box: Rect,
  source: ComposeRecipe['source'],
  turn: number,
  png: boolean,
  title: string,
): Prepared {
  const pw = picture.width
  const ph = picture.height
  const b = clampInside(box, pw, ph)
  // Some of the picture round the box, so the model can see the light, the
  // surface and the grain it has to match: a quarter of the box each way.
  const margin = Math.max(32, Math.round(Math.max(b.w, b.h) * 0.25))
  let area: Rect = { x: b.x - margin, y: b.y - margin, w: b.w + 2 * margin, h: b.h + 2 * margin }
  area = clampInside(area, pw, ph)
  const region = area
  const size = sendSize(region)
  const { canvas: request, k } = sendable(picture, region, size)
  // The starting soft edge; the Edit area card lets it be changed afterwards.
  const feather = Math.max(8, Math.round(Math.min(b.w, b.h) * 0.12))
  // Room for the soft edge on both sides of the box's line.
  const open = { x: b.x - feather, y: b.y - feather, w: b.w + 2 * feather, h: b.h + 2 * feather }
  const mask = maskOf(size, region, null, open)
  return {
    request,
    mask,
    size: 'auto',
    stretch: 1 / k,
    recipe: {
      kind: 'edit', source, turn, pw, ph, out: { x: 0, y: 0, w: pw, h: ph }, region, box: b, feather, png, title,
      sent: { w: request.width, h: request.height },
    },
  }
}

/* ---------------------------------------------------------------- *
 * Lining the model's picture up with the original (2026-10-06)
 * ---------------------------------------------------------------- */

/*
 * The model is asked for 'auto' and answers at about the size it was sent --
 * about, not exactly: an expand came back a few per cent off and the join
 * showed as a step (Huanrui, 2026-10-06). So the answer is not trusted to
 * cover `region` exactly. Wherever the model was told to keep the picture as
 * it was, it redrew it closely, so that part is compared with the original
 * at low resolution and the placement -- position, and a scale each way --
 * that matches best is the one used. Then its colours are brought to the
 * original's over the same part, so the two meet without a step in
 * brightness.
 */

/** Where the model's picture goes, in the turned picture's pixels, and the picture with its colours corrected. */
export interface Fitted {
  place: Rect
  drawn: HTMLCanvasElement
}

const lumaOf = (r: number, g: number, b: number) => 0.299 * r + 0.587 * g + 0.114 * b

/** A source drawn small, as RGBA. */
function small(src: CanvasImageSource, sx: number, sy: number, sw: number, sh: number, w: number, h: number) {
  const { ctx } = canvasOf(w, h)
  ctx.drawImage(src, sx, sy, sw, sh, 0, 0, w, h)
  return ctx.getImageData(0, 0, Math.max(1, Math.round(w)), Math.max(1, Math.round(h))).data
}

/** Whether a point (picture pixels) is one the model was told to keep: outside the box and its soft edge. */
function keptAt(recipe: ComposeRecipe, x: number, y: number): boolean {
  const { pw, ph, feather } = recipe
  if (x < 0 || y < 0 || x >= pw || y >= ph) return false
  if (recipe.boxes) {
    return recipe.boxes.every(
      (b) => x < b.x - feather || x > b.x + b.w + feather || y < b.y - feather || y > b.y + b.h + feather,
    )
  }
  const b = recipe.box
  return x < b.x - feather || x > b.x + b.w + feather || y < b.y - feather || y > b.y + b.h + feather
}

export function fit(picture: HTMLCanvasElement, drawnBitmap: CanvasImageSource & { width: number; height: number }, recipe: ComposeRecipe): Fitted {
  const region = recipe.region
  // The grid the comparison is made on: about 160 across the region.
  const g = 160 / Math.max(region.w, region.h)
  const gw = Math.max(8, Math.round(region.w * g))
  const gh = Math.max(8, Math.round(region.h * g))
  const ref = small(picture, region.x, region.y, region.w, region.h, gw, gh)
  // The model's picture, at the same grid, stretched over the whole region.
  const dw = gw
  const dh = gh
  const drw = small(drawnBitmap, 0, 0, drawnBitmap.width, drawnBitmap.height, dw, dh)
  const dl = new Float32Array(dw * dh)
  for (let i = 0; i < dl.length; i += 1) dl[i] = lumaOf(drw[i * 4]!, drw[i * 4 + 1]!, drw[i * 4 + 2]!)

  // The points to compare: grid cells inside what was kept.
  const pts: { u: number; v: number; luma: number; r: number; g: number; b: number }[] = []
  for (let j = 0; j < gh; j += 1) {
    for (let i = 0; i < gw; i += 1) {
      const x = region.x + ((i + 0.5) / gw) * region.w
      const y = region.y + ((j + 0.5) / gh) * region.h
      if (!keptAt(recipe, x, y)) continue
      const at = (j * gw + i) * 4
      if (ref[at + 3]! < 250) continue
      pts.push({ u: x, v: y, luma: lumaOf(ref[at]!, ref[at + 1]!, ref[at + 2]!), r: ref[at]!, g: ref[at + 1]!, b: ref[at + 2]! })
    }
  }

  const sample = (px: number, py: number, place: Rect): number => {
    const fx = ((px - place.x) / place.w) * dw - 0.5
    const fy = ((py - place.y) / place.h) * dh - 0.5
    if (fx < 0 || fy < 0 || fx > dw - 1 || fy > dh - 1) return NaN
    const x0 = Math.floor(fx)
    const y0 = Math.floor(fy)
    const tx = fx - x0
    const ty = fy - y0
    const x1 = Math.min(dw - 1, x0 + 1)
    const y1 = Math.min(dh - 1, y0 + 1)
    const a = dl[y0 * dw + x0]! * (1 - tx) + dl[y0 * dw + x1]! * tx
    const b = dl[y1 * dw + x0]! * (1 - tx) + dl[y1 * dw + x1]! * tx
    return a * (1 - ty) + b * ty
  }
  /** How alike, -1 to 1 (normalised correlation), over every `step`-th point. */
  const score = (place: Rect, step: number): number => {
    let n = 0
    let sa = 0
    let sb = 0
    let saa = 0
    let sbb = 0
    let sab = 0
    for (let k = 0; k < pts.length; k += step) {
      const p = pts[k]!
      const b = sample(p.u, p.v, place)
      if (Number.isNaN(b)) continue
      const a = p.luma
      n += 1
      sa += a
      sb += b
      saa += a * a
      sbb += b * b
      sab += a * b
    }
    if (n < 30) return -2
    const cov = sab - (sa * sb) / n
    const va = saa - (sa * sa) / n
    const vb = sbb - (sb * sb) / n
    return va > 0 && vb > 0 ? cov / Math.sqrt(va * vb) : -2
  }
  const placeOf = (sx: number, sy: number, tx: number, ty: number): Rect => ({
    x: region.x + tx * region.w - ((sx - 1) * region.w) / 2,
    y: region.y + ty * region.h - ((sy - 1) * region.h) / 2,
    w: region.w * sx,
    h: region.h * sy,
  })

  let best = { sx: 1, sy: 1, tx: 0, ty: 0 }
  let bestScore = pts.length >= 60 ? score(placeOf(1, 1, 0, 0), 1) : -2
  const asIs = bestScore
  if (pts.length >= 60) {
    // Coarse: up to 12% bigger or smaller each way, up to 8% off each way.
    const step = Math.max(1, Math.floor(pts.length / 2500))
    for (let sx = 0.88; sx <= 1.121; sx += 0.04) {
      for (let sy = 0.88; sy <= 1.121; sy += 0.04) {
        for (let tx = -0.08; tx <= 0.081; tx += 0.02) {
          for (let ty = -0.08; ty <= 0.081; ty += 0.02) {
            const s = score(placeOf(sx, sy, tx, ty), step)
            if (s > bestScore) {
              bestScore = s
              best = { sx, sy, tx, ty }
            }
          }
        }
      }
    }
    // Then finer, round the best.
    let ds = 0.02
    let dt = 0.01
    for (let round = 0; round < 4; round += 1) {
      const from = best
      for (const a of [-1, 0, 1]) {
        for (const b of [-1, 0, 1]) {
          for (const c of [-1, 0, 1]) {
            for (const d of [-1, 0, 1]) {
              const cand = { sx: from.sx + a * ds, sy: from.sy + b * ds, tx: from.tx + c * dt, ty: from.ty + d * dt }
              const s = score(placeOf(cand.sx, cand.sy, cand.tx, cand.ty), 1)
              if (s > bestScore) {
                bestScore = s
                best = cand
              }
            }
          }
        }
      }
      ds /= 2
      dt /= 2
    }
  }
  // Only moved when where it was meant to go clearly does not fit and the
  // move clearly does: the model answers at the size it was sent, so the
  // patch nearly always belongs exactly where it was cut from, and a small
  // spurious "better" match moved it off the box (Huanrui, 2026-10-06).
  const place = asIs < 0.9 && bestScore > asIs + 0.05 ? placeOf(best.sx, best.sy, best.tx, best.ty) : region
  console.info('[WonderWorks] canvas edit fit', {
    sent: recipe.sent,
    got: { w: drawnBitmap.width, h: drawnBitmap.height },
    moved: place !== region ? best : null,
    match: Math.round(bestScore * 1000) / 1000,
  })

  // Colours: each channel's level and spread brought to the original's over the kept part.
  const { canvas: drawn, ctx } = canvasOf(drawnBitmap.width, drawnBitmap.height)
  ctx.drawImage(drawnBitmap, 0, 0)
  const drgb = small(drawnBitmap, 0, 0, drawnBitmap.width, drawnBitmap.height, dw, dh)
  const stats = [0, 1, 2].map(() => ({ n: 0, a: 0, aa: 0, b: 0, bb: 0 }))
  for (const p of pts) {
    const fx = Math.floor(((p.u - place.x) / place.w) * dw)
    const fy = Math.floor(((p.v - place.y) / place.h) * dh)
    if (fx < 0 || fy < 0 || fx >= dw || fy >= dh) continue
    const at = (fy * dw + fx) * 4
    const own = [p.r, p.g, p.b]
    for (let c = 0; c < 3; c += 1) {
      const s = stats[c]!
      const a = own[c]!
      const b = drgb[at + c]!
      s.n += 1
      s.a += a
      s.aa += a * a
      s.b += b
      s.bb += b * b
    }
  }
  if (stats[0]!.n >= 60) {
    const fix = stats.map((s) => {
      const ma = s.a / s.n
      const mb = s.b / s.n
      const sa = Math.sqrt(Math.max(1, s.aa / s.n - ma * ma))
      const sb = Math.sqrt(Math.max(1, s.bb / s.n - mb * mb))
      const gain = Math.min(1.25, Math.max(0.8, sa / sb))
      return { gain, offset: ma - gain * mb }
    })
    const image = ctx.getImageData(0, 0, drawn.width, drawn.height)
    const px = image.data
    for (let i = 0; i < px.length; i += 4) {
      for (let c = 0; c < 3; c += 1) {
        const v = px[i + c]! * fix[c]!.gain + fix[c]!.offset
        px[i + c] = v < 0 ? 0 : v > 255 ? 255 : v
      }
    }
    ctx.putImageData(image, 0, 0)
  }
  return { place, drawn }
}

/* ---------------------------------------------------------------- *
 * Putting it back together
 * ---------------------------------------------------------------- */

const smooth = (t: number) => (t <= 0 ? 0 : t >= 1 ? 1 : t * t * (3 - 2 * t))

/**
 * Multiply the alpha of `layer` in `area` (layer pixels) by `alphaAt(x, y)`.
 * Read and written a strip at a time, so a large picture is not one huge
 * array in memory.
 */
function fade(layer: HTMLCanvasElement, area: Rect, alphaAt: (x: number, y: number) => number): void {
  const ctx = layer.getContext('2d')
  if (!ctx) return
  const r = clampInside(area, layer.width, layer.height)
  if (r.w <= 0 || r.h <= 0) return
  const STRIP = 256
  for (let y0 = r.y; y0 < r.y + r.h; y0 += STRIP) {
    const h = Math.min(STRIP, r.y + r.h - y0)
    const image = ctx.getImageData(r.x, y0, r.w, h)
    const px = image.data
    for (let j = 0; j < h; j += 1) {
      for (let i = 0; i < r.w; i += 1) {
        const a = alphaAt(r.x + i + 0.5, y0 + j + 0.5)
        if (a >= 1) continue
        const at = (j * r.w + i) * 4 + 3
        px[at] = Math.round(px[at]! * a)
      }
    }
    ctx.putImageData(image, r.x, y0)
  }
}

/* ---------------------------------------------------------------- *
 * The mask, and the patch it lets through
 * ---------------------------------------------------------------- */

/*
 * The mask is a canvas the size of the patch (`place`, in picture pixels,
 * rounded): white where the model's drawing shows, transparent where the
 * original does, and every shade between. It starts as the box with a soft
 * edge and is painted on afterwards (Edit area card: the brush).
 */

/** A canvas the size of the patch, for a mask or for the patch itself. */
function patchCanvas(place: Rect) {
  return canvasOf(Math.max(1, Math.round(place.w)), Math.max(1, Math.round(place.h)))
}

/** The mask a result starts with: the box, softened across its edge. */
export function defaultMask(recipe: ComposeRecipe, place: Rect): HTMLCanvasElement {
  const { canvas, ctx } = patchCanvas(place)
  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, canvas.width, canvas.height)
  const boxes = recipe.boxes ?? [recipe.box]
  const f = Math.max(1, recipe.feather)
  const sx = canvas.width / place.w
  const sy = canvas.height / place.h
  fade(canvas, { x: 0, y: 0, w: canvas.width, h: canvas.height }, (x, y) => {
    const px = place.x + x / sx
    const py = place.y + y / sy
    // How far inside the nearest box (negative outside), to its nearest edge.
    let d = -Infinity
    for (const box of boxes) {
      d = Math.max(d, Math.min(px - box.x, box.x + box.w - px, py - box.y, box.y + box.h - py))
    }
    return smooth((d + f) / (2 * f))
  })
  return canvas
}

/** The model's drawing, fitted, at the patch's size. */
export function patchLayer(fitted: Fitted): HTMLCanvasElement {
  const { canvas, ctx } = patchCanvas(fitted.place)
  ctx.drawImage(fitted.drawn, 0, 0, canvas.width, canvas.height)
  return canvas
}

/** The patch with the mask applied: what is laid over the original. */
export function maskedPatch(layer: HTMLCanvasElement, mask: HTMLCanvasElement, into?: HTMLCanvasElement): HTMLCanvasElement {
  const canvas = into ?? document.createElement('canvas')
  if (canvas.width !== layer.width || canvas.height !== layer.height) {
    canvas.width = layer.width
    canvas.height = layer.height
  }
  const ctx = canvas.getContext('2d')
  if (!ctx) return canvas
  ctx.globalCompositeOperation = 'copy'
  ctx.drawImage(layer, 0, 0)
  ctx.globalCompositeOperation = 'destination-in'
  ctx.drawImage(mask, 0, 0, canvas.width, canvas.height)
  ctx.globalCompositeOperation = 'source-over'
  return canvas
}

/**
 * One dab of the brush on the mask, at (x, y) in picture pixels: a soft round
 * spot, `radius` picture pixels across from centre to edge, at `flow` (0 to
 * 1) strength. Painting in adds the model's drawing; painting out takes it
 * away again, back to the original.
 */
export function dab(mask: HTMLCanvasElement, place: Rect, x: number, y: number, radius: number, flow: number, out: boolean): void {
  const ctx = mask.getContext('2d')
  if (!ctx) return
  const sx = mask.width / place.w
  const sy = mask.height / place.h
  const cx = (x - place.x) * sx
  const cy = (y - place.y) * sy
  const r = Math.max(1, radius * sx)
  const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, r)
  g.addColorStop(0, `rgba(255,255,255,${flow})`)
  g.addColorStop(0.5, `rgba(255,255,255,${flow * 0.6})`)
  g.addColorStop(1, 'rgba(255,255,255,0)')
  ctx.globalCompositeOperation = out ? 'destination-out' : 'source-over'
  ctx.fillStyle = g
  ctx.beginPath()
  ctx.arc(cx, cy, r, 0, Math.PI * 2)
  ctx.fill()
  ctx.globalCompositeOperation = 'source-over'
}

/** The finished picture: the original, with the masked patch laid over it. */
export function compose(picture: HTMLCanvasElement, fitted: Fitted, recipe: ComposeRecipe, mask?: HTMLCanvasElement): HTMLCanvasElement {
  const { canvas, ctx } = canvasOf(picture.width, picture.height)
  ctx.drawImage(picture, 0, 0)
  const layer = patchLayer(fitted)
  const patch = maskedPatch(layer, mask ?? defaultMask(recipe, fitted.place))
  ctx.drawImage(patch, fitted.place.x, fitted.place.y, fitted.place.w, fitted.place.h)
  return canvas
}

/* ---------------------------------------------------------------- *
 * Edit text (Huanrui, 2026-10-07)
 * ---------------------------------------------------------------- */

/** A copy no longer than `most` pixels on its longer side (the same canvas when it already is). */
export function scaledCopy(picture: HTMLCanvasElement, most: number): HTMLCanvasElement {
  const long = Math.max(picture.width, picture.height)
  if (long <= most) return picture
  const k = most / long
  const { canvas, ctx } = canvasOf(picture.width * k, picture.height * k)
  ctx.drawImage(picture, 0, 0, canvas.width, canvas.height)
  return canvas
}

/** A line of text on the card: what was read, what it should say, and where (fractions of the picture). */
export interface TextLine {
  text: string
  to: string
  box: Rect
}

/** One run's worth of Edit text: one part of the picture, and what changes in it. */
export interface TextGroup {
  /** The changed lines, together, with room round them (picture pixels). What prepareEdit is given. */
  box: Rect
  /** Each changed line with its room: all that is let through over the original. */
  boxes: Rect[]
  feather: number
  changes: { from: string; to: string; where?: string }[]
  /** The other lines the model will see, to be left as they are. */
  keep: string[]
}

const changed = (line: TextLine) => line.to.trim() !== line.text.trim()
const lengthOf = (text: string) => Math.max(1, [...text.trim()].length)

function union(a: Rect, b: Rect): Rect {
  const x = Math.min(a.x, b.x)
  const y = Math.min(a.y, b.y)
  return { x, y, w: Math.max(a.x + a.w, b.x + b.w) - x, h: Math.max(a.y + a.h, b.y + b.h) - y }
}

/** The part prepareEdit sends for a box: the box and a quarter of its longer side each way. */
function areaFor(box: Rect, pw: number, ph: number): Rect {
  const margin = Math.max(32, Math.round(Math.max(box.w, box.h) * 0.25))
  return clampInside({ x: box.x - margin, y: box.y - margin, w: box.w + 2 * margin, h: box.h + 2 * margin }, pw, ph)
}

/**
 * Which changed lines go to the model together.
 *
 * Huanrui's rule: the changed lines are boxed together and sent as one, with
 * every change named in the prompt. Kept from going wrong two ways. Lines
 * far apart are not boxed together when that would shrink the text too far
 * to be redrawn -- the part sent is at most 1,536 pixels across, so a box
 * spanning a whole pack would leave small print a few pixels high -- or when
 * the box would take in more than about half the picture; those go as a
 * second part, a second run. And each line gets room for its new words:
 * longer words, a wider box, growing both ways because which way the line is
 * aligned is not known.
 *
 * `together`: an area was chosen on the card, and everything changed in it
 * goes as one part however far apart the lines are (Huanrui, 2026-10-07) --
 * the person has already said which part of the picture this is about.
 */
export function planText(lines: TextLine[], pw: number, ph: number, together = false): TextGroup[] {
  const px = (r: Rect): Rect => ({ x: r.x * pw, y: r.y * ph, w: r.w * pw, h: r.h * ph })
  const LEAST_HEIGHT = 20
  const MOST_SHARE = 0.45

  const wanted = lines
    .filter(changed)
    .map((line) => {
      const b = px(line.box)
      const h = Math.max(4, Math.min(b.h, b.w))
      const grow = line.to.trim() ? Math.max(0, b.w * (lengthOf(line.to) / lengthOf(line.text) - 1)) : 0
      const padX = Math.max(6, h * 0.6) + grow / 2
      const padY = Math.max(6, h * 0.45)
      const room = clampInside({ x: b.x - padX, y: b.y - padY, w: b.w + 2 * padX, h: b.h + 2 * padY }, pw, ph)
      return { line, room, h }
    })

  const fits = (box: Rect, least: number, count: number) => {
    const area = areaFor(box, pw, ph)
    const long = Math.max(area.w, area.h)
    const k = Math.min(1536, Math.max(1024, long)) / long
    return least * k >= LEAST_HEIGHT && (count === 1 || box.w * box.h <= MOST_SHARE * pw * ph)
  }

  const groups: { box: Rect; members: typeof wanted }[] = []
  for (const one of wanted) {
    const home = together
      ? groups[0]
      : groups.find((group) => {
          const box = union(group.box, one.room)
          const least = Math.min(one.h, ...group.members.map((m) => m.h))
          return fits(box, least, group.members.length + 1)
        })
    if (home) {
      home.box = union(home.box, one.room)
      home.members.push(one)
    } else {
      groups.push({ box: one.room, members: [one] })
    }
  }

  return groups.map((group) => {
    const area = areaFor(group.box, pw, ph)
    const inArea = (r: Rect) => {
      const cx = r.x + r.w / 2
      const cy = r.y + r.h / 2
      return cx >= area.x && cx <= area.x + area.w && cy >= area.y && cy <= area.y + area.h
    }
    const mine = new Set(group.members.map((m) => m.line))
    const seen = lines.filter((line) => inArea(px(line.box)))
    const keep = [...new Set(seen.filter((line) => !mine.has(line)).map((line) => line.text.trim()))]
    // Where, said only when the same words are in the part twice.
    const count = (text: string) => seen.filter((line) => line.text.trim() === text).length
    const whereOf = (r: Rect) => {
      const fx = (r.x + r.w / 2 - area.x) / area.w
      const fy = (r.y + r.h / 2 - area.y) / area.h
      const v = fy < 1 / 3 ? 'at the top' : fy > 2 / 3 ? 'at the bottom' : 'in the middle'
      const h = fx < 1 / 3 ? ' left' : fx > 2 / 3 ? ' right' : ''
      return `${v}${h}`
    }
    const least = Math.min(...group.members.map((m) => m.h))
    return {
      box: group.box,
      boxes: group.members.map((m) => m.room),
      feather: Math.max(4, Math.round(least * 0.25)),
      changes: group.members.map((m) => ({
        from: m.line.text.trim(),
        to: m.line.to.trim(),
        ...(count(m.line.text.trim()) > 1 ? { where: whereOf(px(m.line.box)) } : {}),
      })),
      keep,
    }
  })
}
